import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award, Download, Shield, Calendar, CheckCircle, 
  RefreshCw, ExternalLink, Share2, Copy, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * MY CERTIFICATES PAGE
 * Display and download course completion certificates
 */

const MyCertificates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [downloading, setDownloading] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token');
  
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/certificates/my-certificates`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCertificates(data.certificates || []);
      } else {
        toast.error('Failed to load certificates');
      }
    } catch (e) {
      toast.error('Error loading certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (cert) => {
    setDownloading(cert.course_id);
    try {
      const res = await fetch(`${API_URL}/api/certificates/download/${cert.course_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Certificate_${cert.course_title.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Certificate downloaded!');
      } else {
        toast.error('Failed to download certificate');
      }
    } catch (e) {
      toast.error('Error downloading certificate');
    } finally {
      setDownloading(null);
    }
  };

  const copyVerificationLink = (certNumber) => {
    const link = `${window.location.origin}/verify-certificate/${certNumber}`;
    navigator.clipboard.writeText(link);
    toast.success('Verification link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your certificates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-orange-500 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Award className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">My Certificates</h1>
              <p className="text-white/80 mt-1">Your achievements and course completions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {certificates.length === 0 ? (
          <div className="bg-white rounded-2xl border p-12 text-center">
            <GraduationCap className="w-20 h-20 text-gray-300 mx-auto mb-6" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Certificates Yet</h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              Complete a course to earn your certificate. Your achievements will appear here.
            </p>
            <button
              onClick={() => navigate('/courses')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2"
            >
              <GraduationCap className="w-5 h-5" />
              Browse Courses
            </button>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2">
            {certificates.map((cert) => (
              <div 
                key={cert.id} 
                className="bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`certificate-${cert.id}`}
              >
                {/* Certificate Preview */}
                <div className="bg-gradient-to-br from-purple-50 to-orange-50 p-8 border-b">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Award className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-xs text-purple-600 font-medium uppercase tracking-wider mb-1">Certificate of Completion</p>
                    <h3 className="text-lg font-bold text-gray-900">{cert.course_title}</h3>
                  </div>
                </div>
                
                {/* Certificate Details */}
                <div className="p-5">
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {cert.issued_at ? new Date(cert.issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Date Unknown'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span>Verified</span>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-xs text-gray-500 mb-1">Certificate ID</p>
                    <p className="font-mono text-sm text-gray-700">{cert.certificate_number}</p>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDownload(cert)}
                      disabled={downloading === cert.course_id}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid={`download-${cert.id}`}
                    >
                      {downloading === cert.course_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      Download PDF
                    </button>
                    <button
                      onClick={() => copyVerificationLink(cert.certificate_number)}
                      className="p-2.5 border rounded-lg hover:bg-gray-50"
                      title="Copy verification link"
                    >
                      <Share2 className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Info Section */}
        <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">Verified Certificates</h4>
              <p className="text-blue-700 text-sm">
                Each certificate includes a unique verification ID. Employers and institutions can verify your 
                certificate authenticity at any time using the verification link.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyCertificates;
